"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { IconArrowUpRight, IconPhoto, IconWorld } from "@tabler/icons-react";
import { projectImages, splitTitle, type Project } from "@/constants/projects";

export interface ProjectCardProps {
  project: Project;
  onClick?: () => void;
  /** When set, the card is a link to this page (crawlable) instead of a button */
  href?: string;
  /** "raised" = slightly lighter card, for use inside a black section */
  tone?: "default" | "raised";
}

export default function ProjectCard({ project, onClick, href, tone = "default" }: ProjectCardProps) {
  const { name, kind } = splitTitle(project.title);
  const stack = project.technologies ?? project.tags ?? [];
  const images = projectImages(project);
  const imageCount = images.length;

  const className = `group flex h-full cursor-pointer flex-col overflow-hidden rounded-[20px] border border-white/[0.06] ${tone === "raised" ? "bg-[#0B0D14]" : "bg-black"} transition-all duration-300 hover:-translate-y-1 hover:border-white/15 hover:shadow-[0_20px_60px_-20px_rgba(72,174,239,0.25)]`;
  const content = (
    <>
      <div className="relative m-3 mb-0 overflow-hidden rounded-[14px] bg-[#0E1018]">
        <Image
          src={images[0].src}
          alt={images[0].alt}
          width={1200}
          height={800}
          sizes="(min-width: 1280px) 30vw, (min-width: 640px) 45vw, 90vw"
          className="h-[210px] w-full object-cover transition-transform duration-500 group-hover:scale-[1.04] lg:h-[230px]"
        />
        {imageCount > 1 && (
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/70 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
            <IconPhoto size={13} />
            {imageCount}
          </span>
        )}
        {project.demoUrl && (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/70 px-2.5 py-1 text-[11px] font-semibold text-emerald-300 backdrop-blur">
            <IconWorld size={13} />
            Live
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5">
        {project.category && (
          <span className="w-fit rounded-full bg-[#48AEEF]/10 px-2.5 py-1 text-[12px] font-semibold text-[#48AEEF]">
            {project.category}
          </span>
        )}
        <h3 className="mt-3 text-[21px] font-bold leading-tight">{name}</h3>
        {kind && <p className="mt-1 text-[14px] font-medium text-[#A5B0BC]">{kind}</p>}

        <p className="mt-3 line-clamp-3 text-[15px] leading-relaxed text-[#868E96]">
          {project.description}
        </p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {stack.slice(0, 4).map((t) => (
            <span
              key={t}
              className="rounded-[7px] border border-white/[0.06] bg-[#0E1018] px-2 py-1 text-[12px] font-medium text-[#A5B0BC]"
            >
              {t}
            </span>
          ))}
          {stack.length > 4 && (
            <span className="px-1 py-1 text-[12px] font-medium text-[#5C6670]">+{stack.length - 4}</span>
          )}
        </div>

        <div className="mt-auto pt-5">
          <div className="flex items-center justify-between border-t border-white/[0.06] pt-4">
            <span className="text-[14px] font-semibold text-white/85">View details</span>
            <IconArrowUpRight
              size={18}
              className="text-[#5C6670] transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white"
            />
          </div>
        </div>
      </div>
    </>
  );

  if (href) {
    return (
      <Link href={href} className={className} aria-label={`${name} case study`}>
        {content}
      </Link>
    );
  }

  return (
    <article
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : -1}
      onKeyDown={(e) => {
        if (!onClick) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={className}
    >
      {content}
    </article>
  );
}
